export type PerformanceScope = "full_account" | "our_managed" | "client_managed" | "shared" | "persona_creative";

export type PerformanceRefreshRun = {
  since: string;
  until: string;
  status: "running" | "succeeded" | "failed";
  startedAt: string;
  partial?: boolean;
  limitation?: string | null;
  error?: string | null;
};
export type PerformanceKpiGroup = {
  currency?: string | null;
  event?: string | null;
  spend?: number;
  impressions?: number;
  clicks?: number;
  results?: number;
};

export type PerformanceKpis = {
  currency: string | null;
  spend: number | null;
  results: number | null;
  costPerResult: number | null;
  conversionRate: number | null;
};

/** Complete, server-computed delivery summary used by the Performance Brief headline. */
export type PerformanceHeadlineSummary = {
  resultCurrency?: string | null;
  windowDays: number;
  totalSpend: number;
  totalClicks: number;
  totalResults: number | null;
  costPerResult: number | null;
  conversionRate: number | null;
  spendPerDay: number;
  clicksPerDay: number;
  resultsPerDay: number | null;
  currencyComposition: Array<{ currency: string | null; spend: number }>;
  eventComposition: Array<{ event: string | null; results: number }>;
};

export type PerformanceProductSale = {
  productId: string;
  productName: string | null;
  netSales: number;
  currency: string | null;
};

/** Settings initialize an untouched control once; an explicit user choice wins. */
export function resolvePerformanceScope(current: PerformanceScope | null, saved?: PerformanceScope | null): PerformanceScope {
  return current ?? saved ?? "full_account";
}

export function performanceFindingHref(input: { brandId: string; accountId: string; entityId?: string; adsManagerUrl?: string }): string {
  if (input.adsManagerUrl?.startsWith(`/brands/${encodeURIComponent(input.brandId)}/meta-ads`)) return input.adsManagerUrl;
  const query = new URLSearchParams({ accountId: input.accountId, ...(input.entityId ? { entityId: input.entityId } : {}) });
  return `/brands/${encodeURIComponent(input.brandId)}/meta-ads?${query}`;
}

/** Select one refresh that can truthfully describe this requested history span. */
export function resolvePerformanceHistoryRun<T extends PerformanceRefreshRun>(
  runs: readonly T[],
  since: string,
  until: string,
): T | undefined {
  const relevant = runs.filter(run =>
    (run.since <= since && run.until >= until)
    || (run.since === since && run.until === until && run.status !== "succeeded")
  );
  return [...relevant].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0];
}

export type PerformanceAggregate = {
  currency?: string | null;
  event?: string | null;
  spend?: number | null;
  clicks?: number | null;
  results?: number | null;
};

export function derivePerformanceKpis(input: {
  accountSummary?: readonly PerformanceKpiGroup[] | null;
  eventMetricGroups?: readonly PerformanceKpiGroup[] | null;
}): PerformanceKpis {
  const deliveryGroups = input.accountSummary || [];
  const namedEventGroups = (input.eventMetricGroups || []).filter(group => !!group.event);
  const positiveResultGroups = namedEventGroups.filter(group =>
    !!group.event && [group.spend, group.impressions, group.clicks, group.results].some(value => Number(value || 0) > 0),
  );
  // A single Windsor event row with zero results is still a trustworthy zero.
  // When several event rows exist, only positive rows identify the active event.
  const activeResultGroups = positiveResultGroups.length ? positiveResultGroups : namedEventGroups.length === 1 ? namedEventGroups : [];
  const currencies = new Set(
    [...deliveryGroups.map(group => group.currency), ...activeResultGroups.map(group => group.currency)]
      .filter((currency): currency is string => !!currency),
  );
  const events = new Set(activeResultGroups.map(group => group.event).filter((event): event is string => !!event));
  const deliveryCompatible = deliveryGroups.length > 0
    && deliveryGroups.every(group => !!group.currency)
    && activeResultGroups.every(group => !!group.currency)
    && currencies.size === 1;
  if (!deliveryCompatible) return { currency: null, spend: null, results: null, costPerResult: null, conversionRate: null };

  const spend = deliveryGroups.every(group => Number.isFinite(group.spend))
    ? deliveryGroups.reduce((total, group) => total + Number(group.spend), 0)
    : null;
  const clicks = deliveryGroups.every(group => Number.isFinite(group.clicks))
    ? deliveryGroups.reduce((total, group) => total + Number(group.clicks), 0)
    : null;
  const resultCompatible = activeResultGroups.length > 0 && events.size === 1;
  const results = resultCompatible
    ? activeResultGroups.reduce((total, group) => total + Number(group.results || 0), 0)
    : null;
  return {
    currency: [...currencies][0],
    spend,
    results,
    costPerResult: spend != null && results != null && results > 0 ? spend / results : null,
    conversionRate: clicks != null && clicks > 0 && results != null ? results * 100 / clicks : null,
  };
}

export function calculatePriorPerformance(input: {
  since?: string;
  until?: string;
  priorSince?: string;
  priorUntil?: string;
  currentDelivery: PerformanceAggregate[];
  priorDelivery?: PerformanceAggregate[];
  currentEvents: PerformanceAggregate[];
  priorEvents?: PerformanceAggregate[];
}): { currency?: string; metrics: PriorComparisonMetric[] } {
  const definitions = [
    { key: "costPerResult", label: "Cost per result", format: "currency", lowerIsBetter: true },
    { key: "spendPerDay", label: "Spend per day", format: "currency", lowerIsBetter: true },
    { key: "resultsPerDay", label: "Results per day", format: "number", lowerIsBetter: false },
    { key: "conversionRate", label: "Conversion rate", format: "percent", lowerIsBetter: false },
  ] as const;
  const unavailable = (state: "no_prior" | "unavailable") => ({
    metrics: definitions.map(item => ({ ...item, state })) as PriorComparisonMetric[],
  });

  if (!input.priorDelivery?.length || !input.priorEvents?.length) return unavailable("no_prior");
  const currentDays = inclusivePerformanceDays(input.since, input.until);
  const priorDays = inclusivePerformanceDays(input.priorSince, input.priorUntil);
  if (!currentDays || !priorDays || currentDays !== priorDays) return unavailable("unavailable");
  if (input.currentDelivery.length !== 1 || input.priorDelivery.length !== 1 || input.currentEvents.length !== 1 || input.priorEvents.length !== 1) return unavailable("unavailable");

  const currentDelivery = input.currentDelivery[0];
  const priorDelivery = input.priorDelivery[0];
  const currentEvent = input.currentEvents[0];
  const priorEvent = input.priorEvents[0];
  const currency = currentDelivery.currency || currentEvent.currency || undefined;
  if (!currency || priorDelivery.currency !== currency || currentEvent.currency !== currency || priorEvent.currency !== currency) return unavailable("unavailable");
  if (!currentEvent.event || priorEvent.event !== currentEvent.event) return unavailable("unavailable");
  const raw = {
    costPerResult: [currentEvent.spend, currentEvent.results, priorEvent.spend, priorEvent.results],
    spendPerDay: [currentDelivery.spend, currentDays, priorDelivery.spend, priorDays],
    resultsPerDay: [currentEvent.results, currentDays, priorEvent.results, priorDays],
    conversionRate: [currentEvent.results, currentDelivery.clicks, priorEvent.results, priorDelivery.clicks],
  } as const;

  const metrics = definitions.map(definition => {
    const [currentNumerator, currentDenominator, priorNumerator, priorDenominator] = raw[definition.key];
    if (!finiteNonNegative(currentNumerator) || !finiteNonNegative(priorNumerator) ||
        !finiteNonNegative(currentDenominator) || !finiteNonNegative(priorDenominator) ||
        currentDenominator === 0 || priorDenominator === 0) {
      return { ...definition, state: "unavailable" as const };
    }
    const current = currentNumerator / currentDenominator;
    const prior = priorNumerator / priorDenominator;
    if (!Number.isFinite(current) || !Number.isFinite(prior) || prior === 0) return { ...definition, current, prior, state: "unavailable" as const };
    const change = ((current - prior) / prior) * 100;
    return {
      ...definition,
      current,
      prior,
      change,
      favorable: change === 0 ? undefined : definition.lowerIsBetter ? change < 0 : change > 0,
      state: "ready" as const,
    };
  });
  return { currency, metrics };
}

const finiteNonNegative = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

export type PriorComparisonMetric = {
  key: "costPerResult" | "spendPerDay" | "resultsPerDay" | "conversionRate";
  label: string;
  current?: number;
  prior?: number;
  change?: number;
  favorable?: boolean;
  format: "currency" | "number" | "percent";
  currency?: string;
  state: "ready" | "no_prior" | "unavailable";
};

/** Compare the API's verified headline metrics without rechecking server evidence. */
export function comparePerformanceHeadlines(
  current?: PerformanceHeadlineSummary | null,
  prior?: PerformanceHeadlineSummary | null,
  priorEvidenceIncomplete = false,
  copy: { costPerResultLabel: string; resultsPerDayLabel: string; rateLabel: string } = {
    costPerResultLabel: "Cost per purchase", resultsPerDayLabel: "Purchases per day", rateLabel: "Purchase-to-click rate",
  },
  allowUnequalSpendWindows = false,
): PriorComparisonMetric[] {
  const definitions = [
    { key: "costPerResult", label: copy.costPerResultLabel, format: "currency", lowerIsBetter: true },
    { key: "spendPerDay", label: "Spend per day", format: "currency", lowerIsBetter: true },
    { key: "resultsPerDay", label: copy.resultsPerDayLabel, format: "number", lowerIsBetter: false },
    { key: "conversionRate", label: copy.rateLabel, format: "percent", lowerIsBetter: false },
  ] as const;
  if (!current) return definitions.map(item => ({ ...item, state: "unavailable" }));
  const comparableCurrency = current.currencyComposition.length === 1
    && prior?.currencyComposition.length === 1
    && !!current.currencyComposition[0].currency
    && current.currencyComposition[0].currency === prior.currencyComposition[0].currency;
  const sameWindow = current.windowDays === prior?.windowDays;

  return definitions.map(definition => {
    const currentCurrency = definition.key === "spendPerDay" || current.resultCurrency === undefined
      ? current.currencyComposition.length === 1 ? current.currencyComposition[0].currency : null
      : current.resultCurrency;
    const currentValue = current[definition.key];
    // A missing comparison must not hide independently supported current results.
    // Money still needs its own currency; never label mixed-currency spend USD.
    const currentFields = finiteNonNegative(currentValue)
      && (definition.format !== "currency" || !!currentCurrency)
      ? { current: currentValue, ...(definition.format === "currency" ? { currency: currentCurrency! } : {}) }
      : {};
    if (!prior) return { ...definition, ...currentFields,
      state: priorEvidenceIncomplete ? "unavailable" as const : "no_prior" as const };
    const eligibleCurrency = !!current.resultCurrency && current.resultCurrency === prior.resultCurrency;
    const currencyMatches = definition.key === "spendPerDay" || current.resultCurrency === undefined
      ? comparableCurrency : eligibleCurrency;
    if (priorEvidenceIncomplete || !currencyMatches
      || (!sameWindow && !(allowUnequalSpendWindows && definition.key === "spendPerDay"
        && current.windowDays > 0 && prior.windowDays > 0)))
      return { ...definition, ...currentFields, state: "unavailable" as const };
    const priorValue = prior[definition.key];
    if (!finiteNonNegative(currentValue) || !finiteNonNegative(priorValue))
      return { ...definition, ...currentFields, state: "unavailable" as const };
    if (priorValue === 0)
      return { ...definition, ...currentFields, prior: priorValue, state: "unavailable" as const };
    const change = ((currentValue - priorValue) / priorValue) * 100;
    if (!Number.isFinite(change))
      return { ...definition, ...currentFields, prior: priorValue, state: "unavailable" as const };
    return {
      ...definition,
      ...currentFields,
      prior: priorValue,
      change,
      favorable: change === 0 ? undefined : definition.lowerIsBetter ? change < 0 : change > 0,
      state: "ready" as const,
    };
  });
}

export function inclusivePerformanceDays(since?: string, until?: string): number | null {
  if (!since || !until || !/^\d{4}-\d{2}-\d{2}$/.test(since) || !/^\d{4}-\d{2}-\d{2}$/.test(until)) return null;
  const start = Date.parse(`${since}T00:00:00Z`);
  const end = Date.parse(`${until}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  const days = Math.round((end - start) / 86_400_000) + 1;
  return days > 0 ? days : null;
}
